import { z } from 'zod'
import { DUPLICATE_REASONS, MERGE_FIELDS, MERGE_STATES } from '../patient-merge.schema'

/**
 * Wire DTOs shared by the patient duplicate review & merge procedures (PD-1986). Plain fields only,
 * never FHIR resources. Browser-safe: imports only `zod` and the dependency-free patient-merge.schema.
 */

/** Task `requested|on-hold|rejected|completed|cancelled`, in the reviewer's words. */
export const reviewStatusSchema = z.enum([
  'awaiting',
  'later',
  'not-duplicate',
  'merged',
  'cancelled',
])
export type ReviewStatus = z.infer<typeof reviewStatusSchema>

export const accountStatusSchema = z.enum(['invited', 'active', 'inactive'])
export type AccountStatus = z.infer<typeof accountStatusSchema>

/** The four indicators the keep-record recommendation weighs. */
export const recordSignalsSchema = z.object({
  activeAccount: z.boolean(),
  signedConsent: z.boolean(),
  completedEncounter: z.boolean(),
  identityVerified: z.boolean(),
})
export type RecordSignals = z.infer<typeof recordSignalsSchema>

export const recordSummarySchema = z.object({
  patientId: z.string(),
  name: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  birthDate: z.string().optional(),
  accountStatus: accountStatusSchema,
  loginDisabled: z.boolean(),
  merged: z.boolean(),
})
export type RecordSummary = z.infer<typeof recordSummarySchema>

export const duplicateReviewSummarySchema = z.object({
  reviewId: z.string(),
  status: reviewStatusSchema,
  strength: z.enum(['strong', 'possible']),
  reasons: z.array(z.enum(DUPLICATE_REASONS)),
  reasonText: z.string(),
  records: z.tuple([recordSummarySchema, recordSummarySchema]),
  signals: z.tuple([recordSignalsSchema, recordSignalsSchema]),
  updatedAt: z.string(),
  /**
   * The merge that completed this review (status `merged`), for its result; or,
   * in `duplicateReviewGet` of an open review, its unfinished merge, for progress and Resume (R45).
   */
  mergeId: z.string().optional(),
})
export type DuplicateReviewSummary = z.infer<typeof duplicateReviewSummarySchema>

export const progressNoteSchema = z.object({
  date: z.string().optional(),
  source: z.enum(['behuman', 'imported']),
  signed: z.boolean(),
  encounterFinished: z.boolean(),
  /** BeHuman + signed + finished encounter: the only notes that count as a completed visit. */
  countsAsEncounter: z.boolean(),
})
export type ProgressNote = z.infer<typeof progressNoteSchema>

export const comparisonRecordSchema = z.object({
  summary: recordSummarySchema,
  createdAt: z.string().optional(),
  signals: recordSignalsSchema,
  consents: z.array(
    z.object({
      granted: z.boolean(),
      signedAt: z.string().optional(),
      organizationName: z.string().optional(),
    }),
  ),
  progressNotes: z.array(progressNoteSchema),
  identity: z.object({ verified: z.boolean(), verifiedAt: z.string().optional() }),
  demographics: z.object({
    name: z.string(),
    birthDate: z.string().optional(),
    gender: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().optional(),
    address: z.string().optional(),
  }),
  counts: z.object({
    appointmentsUpcoming: z.number().int(),
    appointmentsPast: z.number().int(),
    encounters: z.number().int(),
    documents: z.number().int(),
    orders: z.number().int(),
    tasks: z.number().int(),
    messages: z.number().int(),
    careQueueTasks: z.number().int(),
    billingCases: z.number().int(),
    importedHgRecords: z.number().int(),
    doseSpotPrescriptions: z.number().int(),
  }),
})
export type ComparisonRecord = z.infer<typeof comparisonRecordSchema>

export const comparisonSchema = z.object({
  review: duplicateReviewSummarySchema,
  records: z.tuple([comparisonRecordSchema, comparisonRecordSchema]),
  differingFields: z.array(z.enum(MERGE_FIELDS)),
  recommendation: z.object({
    recommendedPatientId: z.string().nullable(),
    conflicting: z.boolean(),
    explanation: z.string(),
  }),
  conflicts: z.array(
    z.object({
      kind: z.enum(['dosespot-prescriptions', 'login-will-stop']),
      message: z.string(),
    }),
  ),
  blockers: z.array(z.string()),
})
export type Comparison = z.infer<typeof comparisonSchema>

export const mergeStatusSchema = z.object({
  mergeId: z.string(),
  state: z.enum(MERGE_STATES),
  runStatus: z.enum(['in-progress', 'completed', 'failed']),
  keptPatient: recordSummarySchema,
  mergedPatient: recordSummarySchema,
  mergedAt: z.string().optional(),
  actorName: z.string().optional(),
  reason: z.string().optional(),
  deletesOn: z.string().optional(),
  /** Super admin may Restore: a finished merge, an interrupted one (rolled back), or a failed restore. */
  restorable: z.boolean(),
  /** Resume is offered: still merging, and the run failed or has not been written for 10 minutes. */
  resumable: z.boolean(),
  error: z.string().optional(),
})
export type MergeStatus = z.infer<typeof mergeStatusSchema>
